<template>
   <div data-cy="public-participation-claimer-v1" style="min-height: 12rem">
      <RichTextRenderer v-if="block.data.content" data-cy="richtext-content" :document="block.data.content" />
      <DateInputs
         :id="block.id"
         class="mt-2"
         :isFormatDDMMYY="block.data.dateOfBirth.displayFormat === 'dd-mm-yyyy'"
         isRequired
         :showBlockErrors="state.showInputErrors"
         :existingAnswer="{
            year: state.dateOfBirth.input.year,
            month: state.dateOfBirth.input.month,
            day: state.dateOfBirth.input.day,
         }"
         :is-disabled="state.isDisabled"
         @inputs-update="processDateOfBirth"
      />
      <CodeInput
         id="claim-code"
         name="claim-code"
         class="mt-6 w-1/3"
         :label="$t('survey.blocks.claim_caregiver_flow.claim_code')"
         :code="state.claimCode.input"
         :block-id="block.id"
         isRequired
         :is-disabled="state.isDisabled"
         @input-update="processClaimCode"
         :showErrors="state.showInputErrors"
      />
      <div class="mt-6 flex w-full justify-center">
         <LoadingButton
            data-cy="submit-button"
            size="sm"
            rounded-edges="md"
            :text="$t('survey.blocks.claim_caregiver_flow.button_submit')"
            :is-loading="state.isLoading"
            :is-disabled="state.isDisabled"
            @click="onSubmit"
         />
      </div>
      <InvalidInputsList
         class="mt-6 w-full"
         :error-list="state.errorOnSubmit"
         :class="{ hidden: !state.showInputErrors }"
      />
      <div v-if="state.submissionData" data-cy="review-section" class="mt-6">
         <RichTextRenderer data-cy="richtext-review-section" :document="block.data.reviewSection.content" />
         <div data-cy="submission-data" class="max-h-52 w-full overflow-y-scroll rounded-lg border p-4">
            <template v-for="({ question, answer }, i) of state.submissionData" :key="`question-${i}`">
               <h1 :data-cy="`question-${i}`" class="text-bolder mt-4 text-black">{{ `${i + 1}. ${question}` }}</h1>
               <p :data-cy="`answer-${i}`" class="text-regular text-black">{{ answer }}</p>
            </template>
         </div>
         <CheckboxItem
            id="confirm"
            class="mt-6"
            name="confirm"
            :label="block.data.reviewSection.confirmationLabel"
            :checked="state.hasConfirmed"
            @change="onCheckboxChange"
         />
         <InvalidInputsList class="w-full" :error-list="state.checkboxErrors" :class="{ hidden: !showBlockErrors }" />
         <RichTextRenderer
            data-cy="richtext-contact-us"
            class="mb-6 mt-4"
            :document="block.data.reviewSection.contactUsContent"
         />
      </div>
   </div>
</template>

<script setup lang="ts">
import { useI18n } from "@/vue-i18n/vue-i18n";
import { getFormattedFromDateInput } from "../date/date-utils";
import FlowsApi, { type FlowData } from "@/data/api-layer/flowsApi";
import AnswersAPI, { type AnswerResp } from "@/data/api-layer/answersApi";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";
import type { dateInputsEmits } from "../_inputs/DateInputs.vue";
import FlowParticipationsApi from "@/data/api-layer/flowParticipationsApi";
import type { RichText } from "@/types";

export interface PublicPartClaimAnswerData {
   dataCorrectConfirmation: boolean;
}

interface SubmissionData {
   question: string;
   answer: string;
}

interface Block {
   heading: string;
   blockVersionId: string;
}

export interface PublicPartClaimBlock extends FlowselBlock {
   data: {
      content: RichText;
      dateOfBirth: {
         displayFormat: string;
      };
      reviewSection: {
         content: RichText;
         confirmationLabel: string;
         contactUsContent: RichText;
      };
   };
}

// declarations
// -----------------------------------------
const DATE_FORMAT = "YYYY-MM-DD";
const { t: $t } = useI18n();
const emit = defineEmits(["update-valid", "update-answer"]);

const props = defineProps<{
   flow: FlowData;
   userBlockAnswer: AnswerResp | null;
   block: PublicPartClaimBlock;
   showBlockErrors: boolean;
}>();

const state = reactive({
   dateOfBirth: {
      input: {
         day: "",
         month: "",
         year: "",
      },
      isValid: true,
   },
   claimCode: {
      input: "",
      isValid: true,
   },
   isDisabled: !!props.userBlockAnswer?.metadata?.flowId,
   isLoading: false,
   showInputErrors: false,
   errorOnSubmit: null,
   checkboxErrors: null,
   submissionData: null,
   hasConfirmed: !!props.userBlockAnswer?.metadata?.flowId,
   currentAnswer: props.userBlockAnswer?.data,
});

// Mounted
// -----------------------------------------
onMounted(async () => {
   const submissionData = await getSubmissionData(props.userBlockAnswer?.metadata?.flowId);
   if (submissionData) {
      state.submissionData = submissionData;
   }

   updateAnswer();
});

// Methods
// -----------------------------------------
function processDateOfBirth(dataInputs: dateInputsEmits) {
   state.dateOfBirth.isValid = dataInputs.valid;
   state.errorOnSubmit = null;

   if (dataInputs.valid) {
      state.dateOfBirth.input = {
         year: dataInputs.year,
         month: dataInputs.month,
         day: dataInputs.day,
      };
   }
}

function processClaimCode(codeInput: { value: string; isValid: boolean }) {
   state.claimCode.isValid = codeInput.isValid;
   state.errorOnSubmit = null;

   if (codeInput.isValid) {
      state.claimCode.input = codeInput.value;
   }
}

/*
   On submit we make a call to the backend /claimable-flow-participations/validate,
   using the claim code and DOB to validate the claim.
   If the claim code is valid, the BE returns the "flowId" and "answers" for the flow trying to be claimed in the response.
   This data is passed getSubmissionData method which sets local submissionData (both question & answers) for the review section
 */
async function onSubmit() {
   if (!state.claimCode.isValid || !state.dateOfBirth.isValid) {
      state.showInputErrors = true;
      return;
   }

   state.isLoading = true;

   const invalidSubmissionError = [
      { validator: "invalid", message: $t("survey.blocks.claim_caregiver_flow.invalid_credentials") },
   ];

   try {
      const dateOfBirthAnswer = getFormattedFromDateInput(state.dateOfBirth.input, DATE_FORMAT);
      const currentAnswer = { personal_identifier: dateOfBirthAnswer, claim_code: state.claimCode.input };

      const response = await FlowParticipationsApi.validateFlowParticipationClaim(currentAnswer, props.flow.study?.id);

      // console.info("claimable-flow-response", response.data);
      const { flowId, success, answers, attemptsCount } = response;

      // console.info("flowId", flowId);
      if (success) {
         const submissionData = await getSubmissionData(flowId, answers);
         state.currentAnswer = currentAnswer;

         if (submissionData) {
            state.submissionData = submissionData;
            state.isDisabled = true;
            state.showInputErrors = false;
         }
         state.errorOnSubmit = null;
      } else if (attemptsCount > 5) {
         state.errorOnSubmit = [
            { validator: "invalid", message: $t("survey.blocks.claim_caregiver_flow.too_many_attempts") },
         ];
         state.showInputErrors = true;
      } else {
         state.errorOnSubmit = invalidSubmissionError;
         state.showInputErrors = true;
      }
   } catch (error: any) {
      if (error.response?.data.error_code === "claim_code_invalid") {
         state.errorOnSubmit = invalidSubmissionError;
         state.showInputErrors = true;
      } else {
         console.error("Failed to fetch submission data", error);
      }
   } finally {
      state.isLoading = false;
   }
}

async function getSubmissionData(flowId: string | null, flowAnswers?: AnswerResp[]): Promise<SubmissionData[] | null> {
   if (!flowId) return null;

   try {
      /*
         This method fetches the flow data for a the flow that the user is trying to claim or has been claimed, so that both questions and answers can be displayed together (submission data) in the review section.

         It is called when the component is mounted without the flowAnswers parameter
            - if the user has already claimed the flow, the flow-participation is available to that user, so the participants answers can be fetched
            - if the flow is yet to be claimed, the answers will remain 'undefined' and the function will return null.

         It is also called onSubmit (when the flow-participation is being claimed) with the flowAnswers parameter.
            - In that case the answer data is already returned in the response of the /claimable-flow-participations/validate endpoint, so doesn't need to be re-fetched (that's why the if (!flowAnswers) part).
      */
      const flowResp = await FlowsApi.getFlowById(flowId);
      const blocks = flowResp?.data.data.blocks;

      let answers: AnswerResp[];
      if (!flowAnswers) {
         answers = await AnswersAPI.getAll(flowId);
      } else {
         answers = flowAnswers;
      }

      // console.info("answers", answers);

      if (!blocks || !answers) return null;

      const submissionData = [];
      (blocks as Block[]).forEach((block: Block, i) => {
         const question = block.heading;

         const answer = (() => {
            const rawAnswer = answers.find((a) => a.blockVersionId === block.blockVersionId);

            // skip questions that don't have answer data
            if (!rawAnswer || !rawAnswer.data) return null;

            const { data } = rawAnswer;

            // handle SINGLE-CHOICE and MULTI-CHOICE
            if (data?.choices) {
               return data.choices.map(({ label }) => label).join(", ");

               // handle SHORT-TEXT
            } else if (data?.text) {
               return data.text;

               // handle BOOLEAN-CHOICE
            } else if (data?.boolean !== undefined) {
               if (data.boolean) {
                  return "Yes";
               } else if (data.boolean === null) {
                  return "Unsure";
               }
               return "No";

               // handle SEX
            } else if (data?.sex) {
               return data.sex.charAt(0).toUpperCase() + data.sex.slice(1);
            }

            // skip all unhandled block types
            return null;
         })();

         if (answer) {
            submissionData.push({ question, answer });
         }
      });
      return submissionData.length > 0 ? submissionData : null;
   } catch (error) {
      console.error("Error handling get submission data:", error);
      return null;
   }
}

function onCheckboxChange() {
   state.hasConfirmed = !state.hasConfirmed;

   updateAnswer();
}

function updateAnswer() {
   // only allow to go to the next block after confirming that the data is correct
   if (state.hasConfirmed) {
      emit("update-valid", true);
      emit("update-answer", state.currentAnswer);
      state.checkboxErrors = null;
   } else {
      emit("update-valid", false);
      state.checkboxErrors = [
         {
            inputId: props.block.id,
            validator: "required",
            message: $t("survey.blocks.claim_caregiver_flow.confirm_required_message"),
         },
      ];
   }
}
</script>
